import * as THREE from 'three';
import { POIShape } from './POIShape';
import { POI } from '../poi/POI';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';
import { Logger } from '../core/Logger';
import { Vector3 } from '../types';

/**
 * 🍎 USDZShape: Apple QuickLook AR support with browser fallback
 * 
 * USDZ files are ZIP containers with USD data, designed for Apple's QuickLook AR.
 * Since USDZ files cannot be directly loaded by Three.js GLTFLoader, this shape provides:
 * - Browser fallback (placeholder cube with AR indicator)  
 * - Apple QuickLook AR integration (press 'Q' or tap AR button)
 * - Full 3D model viewing requires dedicated USDZ loader library
 * 
 * Note: For browser 3D viewing, use GLB/GLTF files. USDZ is primarily for iOS AR.
 */
export class USDZShape extends POIShape {
    private isUSDZFile: boolean = false;
    private animationId: number | null = null;
    private loadedScene: THREE.Group | null = null;
    private isModelLoaded: boolean = false;

    constructor(poi: POI, sceneSettings?: SceneSettingsData, appSettings?: AppSettingsData) {
        //console.log(`🔥 USDZShape constructor START for POI: ${poi.name}`);
        super(poi, sceneSettings, appSettings);
        //console.log(`🔥 USDZShape super() completed`);
        
        const uris = poi.poidata.uri?.value;
        const uri = Array.isArray(uris) ? uris[0] : undefined;
        this.isUSDZFile = uri ? uri.toLowerCase().endsWith('.usdz') : false;
        
        //Logger.Instance.Info(`🍎 USDZShape: Constructor called for POI ${poi.name}`, 'USDZShape');
        //Logger.Instance.Info(`🍎 USDZShape: URI: ${uri}`, 'USDZShape');
        //Logger.Instance.Info(`🍎 USDZShape: Is USDZ file: ${this.isUSDZFile}`, 'USDZShape');
        
        if (!this.isUSDZFile) {
            Logger.Instance.Warn(`⚠️ USDZShape: URI does not end with .usdz - this shape is designed for USDZ files`, 'USDZShape');
        }
        
        // Now try to load the USDZ file as a 3D model with proper USDZLoader
        this.loadUSDZModel();
    }

    protected override createGeometry(): THREE.BufferGeometry {
        // 📦 Create a placeholder geometry that will be replaced when model loads
        // If model loading fails, this placeholder will remain visible
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        
        //Logger.Instance.Info('🍎 USDZShape: Created placeholder box geometry (will be replaced if USDZ loads)', 'USDZShape');
        return geometry;
    }

    protected override buildMesh(): void {
        super.buildMesh();
        
        if (this._mesh && !this.isModelLoaded) {
            // 🎨 Style the placeholder to indicate AR content (only if no 3D model loaded)
            const material = (this._mesh as THREE.Mesh).material;
            if (material && 'color' in material) {
                // Subtle blue tint to indicate AR content
                (material as any).color = new THREE.Color(0x4A90E2);
                (material as any).opacity = 0.8;
                (material as any).transparent = true;
            }
            
            // 🍎 Add AR indicator (optional visual cue)
            this.addARIndicator();
        }
        
        //Logger.Instance.Info('🍎 USDZShape: Built placeholder mesh with AR indicator styling', 'USDZShape');
    }

    private addARIndicator(): void {
        if (!this._mesh) return;
        
        try {
            // 📱 Create a simple AR icon above the placeholder
            const iconGeometry = new THREE.RingGeometry(0.15, 0.25, 8);
            const iconMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xFFFFFF, 
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.9
            });
            
            const arIcon = new THREE.Mesh(iconGeometry, iconMaterial);
            arIcon.position.set(0, 0.8, 0);
            arIcon.rotateX(-Math.PI / 2); // Face up
            
            // 🔄 Add subtle rotation animation with cleanup
            const animate = () => {
                if (this._mesh && arIcon.parent) {
                    arIcon.rotation.z += 0.01;
                    this.animationId = requestAnimationFrame(animate);
                }
            };
            animate();
            
            this._mesh.add(arIcon);
            //Logger.Instance.Info('🍎 USDZShape: Added AR indicator icon', 'USDZShape');
        } catch (error) {
            Logger.Instance.Warn(`⚠️ USDZShape: Failed to add AR indicator: ${error}`, 'USDZShape');
        }
    }

    /**
     * 🍎 Get the USDZ file URI for Apple QuickLook AR
     */
    public getUSDZUri(): string | null {
        const uris = this.poi.poidata.uri?.value;
        const uri = Array.isArray(uris) ? uris[0] : undefined;
        if (uri && uri.toLowerCase().endsWith('.usdz')) {
            // Convert to absolute URI if needed
            if (uri.startsWith('http')) {
                return uri;
            } else if (uri.startsWith('/')) {
                return `${window.location.origin}${uri}`;
            } else {
                return `${window.location.origin}/${uri}`;
            }
        }
        return null;
    }

    /**
     * 🍎 Check if this USDZ is ready for Apple QuickLook AR
     */
    public isReadyForAR(): boolean {
        const uri = this.getUSDZUri();
        const isIOSSafari = this.isIOSSafari();
        
        return !!(uri && isIOSSafari);
    }

    private isIOSSafari(): boolean {
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
        return isIOS && isSafari;
    }

    /**
     * 🍎 Override setActive to provide AR feedback
     */
    public override setActive(): void {
        super.setActive();
        
        if (this.isReadyForAR()) {
            //Logger.Instance.Info(`🍎 USDZShape: POI ${this.poi.name} active - Ready for AR! Press 'Q' to view in AR`, 'USDZShape');
            
            // 🎯 Show temporary AR hint (optional)
            this.showARHint();
        } else {
            const uri = this.getUSDZUri();
            const reason = !uri ? 'No USDZ file' : 'Not iOS Safari';
            //Logger.Instance.Info(`🍎 USDZShape: POI ${this.poi.name} active - AR not available (${reason})`, 'USDZShape');
        }
    }

    private showARHint(): void {
        // 💡 Optional: Show a brief AR availability hint
        // This could be implemented as a UI notification
        console.log('🍎 AR Available! Press Q or tap AR button to view in Apple QuickLook AR');
    }

    /**
     * 🎯 Try to load USDZ file as a 3D model
     * Many USDZ files are actually GLB files and can be loaded directly
     */
    private async loadUSDZModel(): Promise<void> {
        const uris = this.poi.poidata.uri?.value || [];
        const uri = uris[0];
        
        if (!uri) {
            Logger.Instance.Warn(`🍎 USDZShape: No model URL provided for POI ${this.poi.name}`, 'USDZShape');
            return;
        }
        
        //Logger.Instance.Info(`🍎 USDZShape: Attempting to load USDZ as 3D model: ${uri}`, 'USDZShape');
        //console.log(`🍎 USDZShape: Starting USDZLoader.loadFile() for ${uri}`);
        
        try {
            // `three-usdz-loader` is CommonJS and imports Three's CJS build. A
            // static import therefore evaluates a second Three.js instance for
            // every Presenter entry point, even when no USDZ exists (notably
            // Map and Terrain). Keep the legacy loader behind the actual USDZ
            // code path so ordinary scenes use the canonical ESM instance only.
            const { USDZLoader } = await import('three-usdz-loader');
            const usdzLoader = new USDZLoader();

            // USDZLoader uses loadFile() not load() - different API!
            // First fetch the USDZ file as a File object
            const response = await fetch(uri);
            if (!response.ok) {
                throw new Error(`Failed to fetch USDZ: ${response.statusText}`);
            }
            const blob = await response.blob();
            const file = new File([blob], uri.split('/').pop() || 'model.usdz', { type: 'application/zip' });
            
            // Create a temporary group for the loader
            const tempGroup = new THREE.Group();
            
            // Load using the proper API
            //console.log(`🍎 USDZShape: USDZLoader loadFile() starting for ${uri}`);
            const loadedModel = await usdzLoader.loadFile(file, tempGroup);
            
            //console.log(`🍎 USDZShape: USDZLoader SUCCESS for ${uri}`, loadedModel);
            //console.log(`🍎 USDZShape: TempGroup children count:`, tempGroup.children.length);
            //console.log(`🍎 USDZShape: TempGroup object:`, tempGroup);
            //console.log(`🍎 USDZShape: LoadedModel object:`, loadedModel);
            
            // Wait a moment for async loading to complete
            await new Promise(resolve => setTimeout(resolve, 100));
            
            //console.log(`🍎 USDZShape: After wait - TempGroup children count:`, tempGroup.children.length);
            
            // FIXED: Use the targetGroup from the USDZInstance, not the tempGroup!
            let scene = tempGroup;
            if (loadedModel && (loadedModel as any).targetGroup) {
                //console.log(`🍎 USDZShape: Using targetGroup from USDZInstance`);
                scene = (loadedModel as any).targetGroup;
                //console.log(`🍎 USDZShape: TargetGroup children count:`, scene.children.length);
            }
            
            this.onModelLoaded({ scene });
            
        } catch (error) {
            // Expected: USDZ files are ZIP containers, not GLB files
            if (error instanceof SyntaxError && error.message.includes('PK')) {
                //console.log(`🍎 USDZShape: USDZ is a ZIP container (expected), using placeholder + AR mode`);
                //Logger.Instance.Info(`🍎 USDZShape: USDZ detected as ZIP container, using AR mode with placeholder`, 'USDZShape');
            } else {
                //console.log(`🍎 USDZShape: Unexpected USDZ loading error:`, error);
                Logger.Instance.Warn(`🍎 USDZShape: Unexpected error loading USDZ: ${error}`, 'USDZShape');
            }
            // Keep placeholder mesh and AR functionality
        }
    }
    
    /**
     * 🎯 Handle successful 3D model loading
     */
    private onModelLoaded(gltf: any): void {
        //console.log(`🍎 USDZShape: onModelLoaded called for POI ${this.poi.name}`);
        //Logger.Instance.Info(`🍎 USDZShape: Successfully loaded USDZ as 3D model for POI ${this.poi.name}`, 'USDZShape');
        
        this.loadedScene = gltf.scene;
        this.isModelLoaded = true;
        //console.log(`🍎 USDZShape: loadedScene:`, this.loadedScene);
        
        // Apply local offset if specified
        if (this.loadedScene) {
            const localTranslationOffset = this.poi.poidata.localTranslationOffset.value ?? Vector3.zero();
            this.loadedScene.position.set(localTranslationOffset.x, localTranslationOffset.y, localTranslationOffset.z);
        }
        
        // Hide placeholder mesh and show loaded model
        if (this._mesh) {
            //console.log(`🍎 USDZShape: Hiding placeholder mesh`);
            this._mesh.visible = false;
            //console.log(`🍎 USDZShape: Placeholder mesh visibility set to:`, this._mesh.visible);
        }
        
        // Add loaded model to the Three.js mesh
        if (this.loadedScene && this._mesh) {
            //console.log(`🍎 USDZShape: Adding loaded 3D model to mesh`);
            //console.log(`🍎 USDZShape: Scene children count:`, this.loadedScene.children.length);
            
            // Debug: Calculate bounding box of loaded scene
            const box = new THREE.Box3().setFromObject(this.loadedScene);
            //console.log(`🍎 USDZShape: Scene bounding box:`, box);
            //console.log(`🍎 USDZShape: Scene size:`, box.getSize(new THREE.Vector3()));
            
            this._mesh.add(this.loadedScene);
            //Logger.Instance.Info(`🍎 USDZShape: Added loaded 3D model to mesh`, 'USDZShape');
        }
        
        // Listen for local offset changes
        this.poi.poidata.localTranslationOffset.on(() => {
            if (this.loadedScene) {
                const newOffset = this.poi.poidata.localTranslationOffset.value ?? Vector3.zero();
                this.loadedScene.position.set(newOffset.x, newOffset.y, newOffset.z);
            }
        });
    }
    
    /**
     * 🧹 Cleanup USDZ-specific resources
     */
    public override dispose(): void {
        // 🧹 Stop animation loop to prevent memory leaks
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        
        // Clean up loaded model
        if (this.loadedScene) {
            this.loadedScene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    if (Array.isArray(child.material)) {
                        child.material.forEach(mat => mat.dispose());
                    } else {
                        child.material?.dispose();
                    }
                }
            });
            this.loadedScene = null;
        }
        
        super.dispose();
        //Logger.Instance.Info('🍎 USDZShape: Disposed USDZ resources', 'USDZShape');
    }
}
